import { useRef } from 'react'
import { Form, Link, useRouteLoaderData, useSubmit } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	NavigationMenu,
	NavigationMenuList,
	NavigationMenuItem,
	NavigationMenuLink,
} from '#app/components/ui/navigation-menu.tsx'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '#app/components/ui/sheet.tsx'
import { type loader } from '#app/root.tsx'
import { ThemeSwitch } from '#app/routes/resources+/theme-switch.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

export function MarketingHeader() {
	const user = useOptionalUser()
	const data = useRouteLoaderData<typeof loader>('root')

	return (
		<>
			<header className="bg-background/95 supports-[backdrop-filter]:bg-background/60 fixed inset-x-0 top-0 z-50 flex h-16 shrink-0 items-center gap-2 border-b backdrop-blur">
				<div className="flex flex-1 items-center gap-2 px-4">
					{/* Mobile menu */}
					<Sheet>
						<SheetTrigger asChild className="md:hidden">
							<Button variant="ghost" size="icon" className="-ml-1">
								<Icon name="hamburger-menu" />
								<span className="sr-only">Open navigation menu</span>
							</Button>
						</SheetTrigger>
						<SheetContent side="left" className="w-[300px] sm:w-[400px]">
							<SheetHeader>
								<SheetTitle>Menu</SheetTitle>
							</SheetHeader>
							<nav className="mt-6 flex flex-col">
								<Link
									to="/rentals"
									className="hover:bg-accent flex items-center gap-3 rounded-md px-3 py-2 text-base font-medium transition-colors"
								>
									<Icon name="file-text" size="md" />
									Rentals
								</Link>
								<Link
									to="/rentals/categories"
									className="hover:bg-accent flex items-center gap-3 rounded-md px-3 py-2 text-base font-medium transition-colors"
								>
									<Icon name="layers" size="md" />
									Categories
								</Link>
								<Link
									to="/tools"
									className="hover:bg-accent flex items-center gap-3 rounded-md px-3 py-2 text-base font-medium transition-colors"
								>
									<Icon name="drill" size="md" />
									Tools
								</Link>

								<div className="border-border my-2 border-t" />

								{user ? (
									<>
										<Link
											to="/manage"
											className="hover:bg-accent flex items-center gap-3 rounded-md px-3 py-2 text-base font-medium transition-colors"
										>
											<Icon name="settings-2" size="md" />
											Manage Listings
										</Link>
										{user.roles.some((role) => role.name === 'ADMIN') && (
											<Link
												to="/dashboard"
												className="hover:bg-accent flex items-center gap-3 rounded-md px-3 py-2 text-base font-medium transition-colors"
											>
												<Icon name="layout-dashboard" size="md" />
												Dashboard
											</Link>
										)}
										<Link
											to="/profile"
											className="hover:bg-accent flex items-center gap-3 rounded-md px-3 py-2 text-base font-medium transition-colors"
										>
											<Icon name="user" size="md" />
											Profile
										</Link>
									</>
								) : (
									<>
										<Link
											to="/login?redirectTo=/manage"
											className="hover:bg-accent flex items-center gap-3 rounded-md px-3 py-2 text-base font-medium transition-colors"
										>
											<Icon name="user" size="md" />
											Log In
										</Link>
										<Link
											to="/submit-rental"
											className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-3 rounded-md px-3 py-2 text-base font-medium transition-colors"
										>
											<Icon name="plus" size="md" />
											Submit Rental
										</Link>
									</>
								)}
							</nav>
						</SheetContent>
					</Sheet>

					<Link
						to="/"
						className="flex items-center text-3xl leading-none font-black tracking-tight transition-all duration-300 hover:scale-105"
					>
						<span className="text-red-500">RENT</span>
						DFW
					</Link>

					{/* Right side actions */}
					<div className="ml-auto flex items-center gap-2">
						<NavigationMenu className="hidden md:flex">
							<NavigationMenuList>
								<NavigationMenuItem>
									<NavigationMenuLink asChild>
										<Link to="/rentals">Rentals</Link>
									</NavigationMenuLink>
								</NavigationMenuItem>
								<NavigationMenuItem>
									<NavigationMenuLink asChild>
										<Link to="/rentals/categories">Categories</Link>
									</NavigationMenuLink>
								</NavigationMenuItem>
								<NavigationMenuItem>
									<NavigationMenuLink asChild>
										<Link to="/tools">Tools</Link>
									</NavigationMenuLink>
								</NavigationMenuItem>
							</NavigationMenuList>
						</NavigationMenu>

						<div className="hidden md:block">
							{user ? (
								<div className="flex items-center gap-2">
									<Button asChild size="sm">
										<Link to="/manage">Manage Listings</Link>
									</Button>
									<UserDropdown />
								</div>
							) : (
								<>
									<Button asChild variant="ghost" size="sm">
										<Link to="/login?redirectTo=/manage">Log In</Link>
									</Button>
									<Button asChild size="sm">
										<Link to="/submit-rental">Submit Rental</Link>
									</Button>
								</>
							)}
						</div>
						<ThemeSwitch userPreference={data?.requestInfo.userPrefs.theme} />
					</div>
				</div>
			</header>
			<div aria-hidden className="h-16" />
		</>
	)
}

function UserDropdown() {
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	const user = useOptionalUser()

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost">
					<Icon name="user" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={1} align="end">
					{user?.roles.some((role) => role.name === 'ADMIN') && (
						<DropdownMenuItem asChild>
							<Link prefetch="intent" to={`/dashboard`}>
								<Icon name="layout-dashboard" size="xs">
									Dashboard
								</Icon>
							</Link>
						</DropdownMenuItem>
					)}
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/profile`}>
							<Icon name="user" size="xs">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem
						asChild
						// this prevents the menu from closing before the form submission is completed
						onSelect={(event) => {
							event.preventDefault()
							void submit(formRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={formRef}>
							<Icon name="exit" size="xs">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
